import { getMap } from './mapInstance.js';
import { makeDraggable, makeModalDraggable } from './mapUtils.js';
import { Vector as VectorLayer } from 'ol/layer';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Fill, Stroke, Circle as CircleStyle } from 'ol/style.js';
import * as XLSX from 'xlsx';
import { API_URL } from './config.js';
import { updateOwnDataSidebar } from './layerInfoSidebar.js';
import { secureFetch } from './securityUtil.js';
import { placeFloatingDialog } from './dialogs.js';

// Backend endpoints
const API_SINGLE = `${API_URL}postal-layer/search`;
const API_BATCH  = `${API_URL}postal-layer/batch-search`;

// Default selections
const DEFAULT_COUNTRY_ID = 1;
const DEFAULT_LANGUAGE_ID = 1;

// Store Own Data layers globally
export const ownDataLayers = [];

// ------------------- Upload Module Setup -------------------
export function setupUploadModule(sidebar) {
  const panel = document.createElement('div');
  panel.id = 'upload-panel';
  panel.className = 'tool-panel d-none shadow rounded border bg-white p-3';
  panel.style.position = 'absolute';
  panel.style.top = '120px';
  panel.style.left = '120px';
  panel.style.zIndex = '1100';
  panel.style.width = '320px';

  const header = document.createElement('div');
  header.className = 'd-flex justify-content-between align-items-center mb-2';
  header.innerHTML = `<h6 class="mb-0">Upload Own Data</h6><button type="button" class="btn-close"></button>`;
  header.querySelector('.btn-close').addEventListener('click', () => panel.classList.add('d-none'));
  header.style.cursor = 'move';
  panel.appendChild(header);

  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.accept = '.csv,.xlsx';
  fileInput.className = 'form-control mb-3';
  panel.appendChild(fileInput);

  document.body.appendChild(panel);
  makeDraggable(panel, header);

  // ------------------- File parsing -------------------
  fileInput.addEventListener('change', async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = await readExcel(file);
    if (!data.length) return alert('No data found');
    openMappingUI(data);
  });

  function openMappingUI(jsonData) {
    const mappingModal = document.getElementById('mapping-modal') || createMappingModal();
    const mappingBody = mappingModal.querySelector('#mapping-body');

    mappingBody.innerHTML = '';
    const columns = Object.keys(jsonData[0]);
    columns.forEach(col => {
      const div = document.createElement('div');
      div.className = 'mb-2';
      div.innerHTML = `
        <label class="form-label mb-0">${col}</label>
        <select class="form-select form-select-sm mapping-select" data-col="${col}">
          <option value="">Ignore</option>
          <option value="postalCode">Postal Code</option>
          <option value="houseNumber">House Number</option>
          <option value="street">Street</option>
          <option value="city">City</option>
        </select>
      `;
      mappingBody.appendChild(div);
    });

    mappingModal.classList.remove('d-none');
    requestAnimationFrame(() => placeFloatingDialog(mappingModal));

    const confirmBtn = mappingModal.querySelector('#confirm-mapping-btn');
    confirmBtn.onclick = async () => {
      const mapping = {};
      mappingBody.querySelectorAll('.mapping-select').forEach(sel => {
        if (sel.value) mapping[sel.value] = sel.dataset.col;
      });
      mappingModal.classList.add('d-none');
      await sendDataToBackend(jsonData, mapping);
    };
  }

  // ------------------- Send Data to Backend -------------------
  async function sendDataToBackend(jsonData, mapping) {
    for (const row of jsonData) {
      const rowValues = {};
      Object.entries(mapping).forEach(([key, colName]) => rowValues[key] = row[colName]);

      const payload = {
        countryId: DEFAULT_COUNTRY_ID,
        languageId: DEFAULT_LANGUAGE_ID,
        rowValues
      };

      try {
        const response = await secureFetch(API_SINGLE, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error(`Backend error ${response.status}`);
        const geojson = await response.json();
        addOwnDataLayer(geojson, `Own Data ${ownDataLayers.length + 1}`);
      } catch (err) {
        console.error('Search failed', err);
      }
    }
  }

  // ------------------- Add Own Data Layer -------------------
  function addOwnDataLayer(geojson, title) {

            const map = getMap();
      if (!map) return;

    if (!geojson || !geojson.features || geojson.features.length === 0) {
      console.warn('No features returned from backend');
      alert('No geometry found for the given postal code.');
      return;
    }

    const vectorLayer = new VectorLayer({
      source: new VectorSource({
        features: new GeoJSON().readFeatures(geojson, {
          dataProjection: 'EPSG:4326',
          featureProjection: map.getView().getProjection()
        })
      }),
      style: new Style({
        stroke: new Stroke({ color: 'red', width: 2 }),
        fill: new Fill({ color: 'rgba(255,0,0,0.1)' }),
        image: new CircleStyle({ radius: 5, fill: new Fill({ color: '#FF0000' }) })
      }),
      title: title,
      group: 'ownData'
    });

    map.addLayer(vectorLayer);
    ownDataLayers.push(vectorLayer);
    updateOwnDataSidebar();

    const extent = vectorLayer.getSource().getExtent();
    if (extent) map.getView().fit(extent, { padding: [20, 20, 20, 20], maxZoom: 15 });
  }

  return panel;
}

// ------------------- Excel Reader -------------------
async function readExcel(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      resolve(XLSX.utils.sheet_to_json(worksheet, { defval: '' }));
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file);
  });
}

// ------------------- Mapping Modal -------------------
function createMappingModal() {
  const mappingModal = document.createElement('div');
  mappingModal.id = 'mapping-modal';
  mappingModal.className = 'd-none shadow rounded border bg-white p-3';
  mappingModal.style.position = 'absolute';
  mappingModal.style.top = '150px';
  mappingModal.style.left = '150px';
  mappingModal.style.width = '400px';
  mappingModal.style.zIndex = '1200';

  const mappingHeader = document.createElement('div');
  mappingHeader.className = 'd-flex justify-content-between align-items-center mb-2 bg-light px-2 py-1';
  mappingHeader.innerHTML = `<strong>Map Columns</strong><button type="button" class="btn-close btn-sm"></button>`;
  mappingHeader.querySelector('.btn-close').addEventListener('click', () => mappingModal.classList.add('d-none'));
  mappingHeader.style.cursor = 'move';
  mappingModal.appendChild(mappingHeader);

  const mappingBody = document.createElement('div');
  mappingBody.id = 'mapping-body';
  mappingBody.style.maxHeight = '300px';
  mappingBody.style.overflowY = 'auto';
  mappingModal.appendChild(mappingBody);

  const confirmBtn = document.createElement('button');
  confirmBtn.id = 'confirm-mapping-btn';
  confirmBtn.className = 'btn btn-primary w-100 mt-2';
  confirmBtn.textContent = 'Confirm Mapping';
  mappingModal.appendChild(confirmBtn);

  document.body.appendChild(mappingModal);
  makeModalDraggable(mappingModal, mappingHeader);

  return mappingModal;
}
